import React from "react";
import Header from "@launchmetrics/dmr-header-component/src/Header";
import Footer from "@launchmetrics/dmr-footer-component/src/Footer";
import { UserModel, ProjectModel, PlatformModel, MenuModel } from "@launchmetrics/dmr-header-component/src/Model/header-models";
import MenuService from '../services/MenuService';
import { DataLabelingProjectModel } from "../services/ProjectService";
import { Container } from "react-bootstrap";
import { useNotification } from '../contexts/NotificationContext';
import { CommonLibrary } from '../contexts/CommonContext'; // Adjust the path as necessary
import { useProjectContext } from "../contexts/ProjectContext";
import { useNavigate } from "react-router-dom";
import { ActiveProjectContext, ProjectLoadStatus } from '../contexts/ActiveProjectContext';
import { menuForDlpFeature } from '../utils/dlpFeatureMenu';

interface MainLayoutProps {
    children: JSX.Element;
}

const MainLayout = ({ children }: MainLayoutProps) => {
    const logo = `${process.env.PUBLIC_URL}/logo512.png`;
    
    const notifications = useNotification();
    const navigate = useNavigate();
    const comonLibrary = new CommonLibrary();
    const [projects, setProjects] = React.useState<DataLabelingProjectModel[]>(comonLibrary.UserProjects);
    const [selectedProject, setSelectedProject] = React.useState<DataLabelingProjectModel | undefined>(comonLibrary.ActiveProject);
    const [projectStatus, setProjectStatus] = React.useState<ProjectLoadStatus>('loading');
    const { loadProjects } = useProjectContext();
    const loadProjectsRef = React.useRef(loadProjects);
    const notifyRef = React.useRef(notifications.showNotification);
    loadProjectsRef.current = loadProjects;
    notifyRef.current = notifications.showNotification;
    const user: UserModel = {
        UserId: localStorage.getItem('sso-auth-userId') || "0",
        UserName: localStorage.getItem('sso-auth-user') || "-",
        LogoutLink: `${process.env.REACT_APP_DEPLOY_PATH}/logout`,
    };

    let platforms = [];
    const platform1: PlatformModel = {
        Id: 1,
        Description: "Labeling",
        Link: `${process.env.REACT_APP_DEPLOY_PATH}/logout`,
    };
    platforms.push(platform1);

    const platform2: PlatformModel = {
        Id: 2,
        Description: "WebDigital",
        Link: `${process.env.REACT_APP_WEBDIGITAL}`
    };
    platforms.push(platform2);

    // Set the menu items

    const [menuState, setMenuState] = React.useState<{ project?: DataLabelingProjectModel, items: MenuModel[] }>({ items: [] });

    React.useEffect(() => {
        let current = true;
        // Always refresh properties once on layout mount: cached feature flags may be stale.
        loadProjectsRef.current().then(loaded => {
            if (!current) return;
            if (!loaded) {
                setProjectStatus('error');
                return;
            }
            const projectId = parseInt(localStorage.getItem('projectId') || '0', 10);
            setProjects(loaded);
            setSelectedProject(loaded.find(p => p.Id === projectId) ?? loaded[0]);
            setProjectStatus('ready');
        }).catch(() => {
            if (current) setProjectStatus('error');
        });
        return () => { current = false; };
    }, []);

    React.useEffect(() => {
        setMenuState({ items: [] });
        if (projectStatus !== 'ready' || !selectedProject) return;
        let current = true;
        // ProjectContext updates projectId before this call: no bootstrap menu for project 0.
        const menuServiceInstance = new MenuService();
        menuServiceInstance.GetMenu().then(items => {
            if (current) setMenuState({ project: selectedProject, items });
        }).catch(() => {
            if (current) notifyRef.current('Error loading menu', 'error');
        });
        return () => { current = false; };
    }, [projectStatus, selectedProject]);

   

    const [, setSelectedPlatform] = React.useState<PlatformModel>(platform1);
    const onPlatformSelect = (platform: PlatformModel) => {
        setSelectedPlatform(platform);
    }

    const onProjectSelect = (selectedProject: ProjectModel) => {
        const currentProject = projects.find(p => p.Id === selectedProject.Id);
        if (!currentProject) return;
        setSelectedProject(currentProject);
        localStorage.setItem('projectId', currentProject.Id.toString());
        if (currentProject) {
            sessionStorage.setItem('activeProject', JSON.stringify(currentProject));
            sessionStorage.removeItem('startDate');
            sessionStorage.removeItem('endDate');
        }
        notifications.showNotification(`Project ${selectedProject.Description} selected`, 'info');
        navigate("/dashboard");
    }


    return (
        <div className="app-main-content-wrapper">
            <Header logo={logo} user={user} activeProject={selectedProject} projects={projects} platforms={platforms}
                menu={menuForDlpFeature(menuState.project === selectedProject ? menuState.items : [], projectStatus === 'ready' && selectedProject?.DLPConfigTool === true)}
                onProjectSelect={onProjectSelect} onPlatformSelect={onPlatformSelect} />
            <Container className="content-body" key={selectedProject?.Id}>
                <ActiveProjectContext.Provider value={{ project: selectedProject, status: projectStatus }}>
                    {children}
                </ActiveProjectContext.Provider>
            </Container>
            <Footer description="Datalabeling Platform v 2.0" />
        </div>
    );
}

export default MainLayout;